"use client";

import React from "react";
import { cn } from "@repo/utils/cn";
import { tv, VariantProps } from "tailwind-variants";
import { sectionWrapper } from "../sectionWrappers";
import { SectionTitle, SectionTitleProps } from "../SectionTitle";
import { Accordion, AccordionItem } from "../Accordion";

const faqSection = tv({
  base: "uipm:w-full uipm:px-[var(--container-margin)]",
});

const faqLayout = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:lg:flex-row uipm:gap-12 uipm:items-start",
    "uipm:w-full uipm:max-w-[1200px] uipm:mx-auto",
  ),
});

const leftColumn = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:gap-8",
    "uipm:w-full uipm:lg:w-1/2 uipm:lg:flex-shrink-0 uipm:lg:max-w-[920px]",
    "uipm:pr-0 uipm:lg:pr-16",
  ),
});

const rightColumn = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:gap-6",
    "uipm:w-full uipm:lg:w-1/2 uipm:lg:flex-shrink-0",
  ),
});

export interface FaqSectionProps extends VariantProps<typeof sectionWrapper> {
  id?: string;
  head?: SectionTitleProps;
  questions?: AccordionItem[];
  className?: string;
  onItemToggle?: (index: number, title: string, isOpen: boolean) => void;
  [key: string]: any;
}

export const FaqSection = React.forwardRef<HTMLElement, FaqSectionProps>(
  (
    {
      head,
      questions = [],
      backgroundColor = "none",
      topPadding = "none",
      bottomPadding = "none",
      className,
      id,
      onItemToggle,
      ...props
    },
    ref,
  ) => {
    return (
      <section
        id={id}
        ref={ref}
        className={sectionWrapper({
          backgroundColor,
          topPadding,
          bottomPadding,
        })}
        {...props}
      >
        <div className={faqSection()}>
          <div className={cn(faqLayout(), className)}>
            <div className={leftColumn()}>
              {head && <SectionTitle {...head} />}
            </div>

            <div className={rightColumn()}>
              {questions.length > 0 && (
                <Accordion items={questions} onItemToggle={onItemToggle} />
              )}
            </div>
          </div>
        </div>
      </section>
    );
  },
);

FaqSection.displayName = "FaqSection";
